/**
 * ToneAdjustmentPanel Component
 * Panel para ajustar o tom do texto selecionado
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { X, Wand2, Loader2 } from 'lucide-react';

interface ToneAdjustmentPanelProps {
  visible: boolean;
  selectedText: string;
  isProcessing: boolean;
  onClose: () => void;
  onAdjustTone: (options: {
    formality: string;
    documentType: string;
    tone: string;
  }) => void;
}

export const ToneAdjustmentPanel: React.FC<ToneAdjustmentPanelProps> = ({
  visible,
  selectedText,
  isProcessing,
  onClose,
  onAdjustTone
}) => {
  const [formality, setFormality] = useState('formal');
  const [documentType, setDocumentType] = useState('geral');
  const [tone, setTone] = useState('neutro');

  const handleSubmit = () => {
    onAdjustTone({ formality, documentType, tone });
  };

  if (!visible) return null;

  return (
    <div 
      className="absolute top-0 right-full mr-4 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-[10001] max-h-96 overflow-y-auto"
      data-component="tone-adjustment-panel"
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Ajustar Tom
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {selectedText && (
        <div className="mb-4 p-2 bg-gray-50 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
          <div className="font-medium mb-1">Texto selecionado:</div>
          <div className="max-h-16 overflow-y-auto">
            "{selectedText.substring(0, 100)}{selectedText.length > 100 ? '...' : ''}"
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4">
          <div className="space-y-2">
            <Label htmlFor="formality" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Formalidade
            </Label>
            <Select value={formality} onValueChange={setFormality}>
              <SelectTrigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
                <SelectValue placeholder="Selecione a formalidade" />
              </SelectTrigger>
              <SelectContent data-component="tone-adjustment-panel">
                <SelectItem value="informal">Informal</SelectItem>
                <SelectItem value="formal">Formal</SelectItem>
                <SelectItem value="muito-formal">Muito Formal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="documentType" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Tipo de Documento
            </Label>
            <Select value={documentType} onValueChange={setDocumentType}>
              <SelectTrigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
                <SelectValue placeholder="Selecione o tipo" />
              </SelectTrigger>
              <SelectContent data-component="tone-adjustment-panel">
                <SelectItem value="geral">Geral</SelectItem>
                <SelectItem value="peticao">Petição</SelectItem>
                <SelectItem value="contrato">Contrato</SelectItem>
                <SelectItem value="parecer">Parecer</SelectItem>
                <SelectItem value="ata">Ata</SelectItem>
                <SelectItem value="oficio">Ofício</SelectItem>
                <SelectItem value="memorando">Memorando</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tone" className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              Tom
            </Label>
            <Select value={tone} onValueChange={setTone}>
              <SelectTrigger className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1">
                <SelectValue placeholder="Selecione o tom" />
              </SelectTrigger>
              <SelectContent data-component="tone-adjustment-panel">
                <SelectItem value="neutro">Neutro</SelectItem>
                <SelectItem value="persuasivo">Persuasivo</SelectItem>
                <SelectItem value="assertivo">Assertivo</SelectItem>
                <SelectItem value="conciliatorio">Conciliatório</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button 
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2 w-full"
          onClick={handleSubmit}
          disabled={isProcessing || !selectedText.trim()}
        >
          {isProcessing ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Wand2 className="h-4 w-4 mr-2" />
          )}
          {isProcessing ? 'Ajustando Tom...' : 'Ajustar Tom'}
        </Button>
      </div>
    </div>
  );
};

export default ToneAdjustmentPanel;